(() => {
  if (window.EFMontageV4Loaded) return;
  window.EFMontageV4Loaded = true;

  let busy = false;

  const rootUrl = () => (window.FoxTheme?.routes?.root_url || '/').replace(/\/?$/, '/');
  const cartUrl = () => window.FoxTheme?.routes?.cart_url || '/cart';
  const addUrl = () => window.FoxTheme?.routes?.cart_add_url || `${rootUrl()}cart/add.js`;
  const changeUrl = () => window.FoxTheme?.routes?.cart_change_url || `${rootUrl()}cart/change.js`;
  const component = () => document.querySelector('[data-ef-montage]');

  async function jsonFetch(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    const text = await response.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (_) {}

    if (!response.ok) {
      throw new Error(data.description || data.message || text || `HTTP ${response.status}`);
    }
    return data;
  }

  async function getCart() {
    return jsonFetch(`${rootUrl()}cart.js`);
  }

  async function addVariant(variantId) {
    return jsonFetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity: 1,
          properties: { '_EF Montage Protection': 'true' }
        }]
      })
    });
  }

  async function changeItem(key, quantity) {
    return jsonFetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Number(quantity) })
    });
  }

  function planItems(cart, productId) {
    return (cart.items || []).filter(
      item => Number(item.product_id) === Number(productId)
    );
  }

  function setStatus(el, message) {
    const status = el?.querySelector('[data-ef-montage-status]');
    if (status) status.textContent = message || '';
  }

  function isCartPage() {
    const path = window.location.pathname.replace(/\/+$/, '');
    const cartPath = String(cartUrl()).split('?')[0].replace(/\/+$/, '');
    return path === cartPath || path === '/cart';
  }

  function refreshUI() {
    // On the full cart page Hyper needs the complete server-rendered cart
    // after our second (Montage) cart mutation.
    if (isCartPage()) {
      window.location.reload();
      return;
    }

    // Drawer: use Hyper's native refresh event.
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: { open: true }
    }));
  }

  async function normalizePlan({ explicitChoice = null } = {}) {
    if (busy) return;

    const el = component();
    if (!el) return;

    const productId = Number(el.dataset.planProductId || 0);
    const desiredVariantId = Number(el.dataset.desiredVariantId || 0);
    const planShownInLiquid = el.dataset.planInCart === 'true';

    if (!productId) return;

    // Never opt a customer in automatically.
    // Automatic runs only maintain a plan that already exists.
    const shouldHavePlan =
      explicitChoice === true ? true :
      explicitChoice === false ? false :
      planShownInLiquid;

    busy = true;

    const toggle = el.querySelector('[data-ef-montage-toggle]');
    if (toggle) toggle.disabled = true;

    try {
      let cart = await getCart();
      let plans = planItems(cart, productId);

      // Customer opted out OR no eligible subtotal/tier remains.
      if (!shouldHavePlan || !desiredVariantId) {
        if (!plans.length) {
          setStatus(el, '');
          return;
        }

        setStatus(el, 'Updating protection…');
        for (const item of plans) {
          await changeItem(item.key, 0);
        }
        refreshUI();
        return;
      }

      const correct = plans.find(
        item => Number(item.variant_id) === desiredVariantId
      );

      const alreadyPerfect =
        plans.length === 1 &&
        correct &&
        Number(correct.quantity) === 1;

      if (alreadyPerfect) {
        setStatus(el, '');
        if (toggle) toggle.checked = true;
        return;
      }

      setStatus(el, explicitChoice === true ? 'Adding protection…' : 'Updating protection…');

      // Remove wrong tiers / duplicates.
      for (const item of plans) {
        if (!correct || item.key !== correct.key) {
          await changeItem(item.key, 0);
        }
      }

      // Re-read after removals.
      cart = await getCart();
      plans = planItems(cart, productId);
      const currentCorrect = plans.find(
        item => Number(item.variant_id) === desiredVariantId
      );

      if (!currentCorrect) {
        await addVariant(desiredVariantId);
      } else if (Number(currentCorrect.quantity) !== 1) {
        await changeItem(currentCorrect.key, 1);
      }

      // Verify before refreshing UI.
      cart = await getCart();
      plans = planItems(cart, productId);
      const verified = plans.find(
        item => Number(item.variant_id) === desiredVariantId && Number(item.quantity) === 1
      );

      if (!verified) {
        throw new Error('Protection plan could not be synchronized.');
      }

      refreshUI();
    } catch (error) {
      console.error('EF Montage V4:', error);
      setStatus(el, error.message || 'Could not update protection. Please try again.');
      if (toggle && explicitChoice !== null) {
        toggle.checked = !explicitChoice;
      }
    } finally {
      busy = false;
      if (toggle) toggle.disabled = false;
    }
  }

  // Explicit customer opt-in / opt-out.
  document.addEventListener('change', (event) => {
    const toggle = event.target.closest?.('[data-ef-montage-toggle]');
    if (!toggle) return;
    normalizePlan({ explicitChoice: toggle.checked });
  });

  // Hyper fires cart:updated after quantity changes/removals and after
  // replacing the cart section HTML. Maintain Montage only if already opted in.
  document.addEventListener('cart:updated', () => {
    window.setTimeout(() => normalizePlan(), 0);
  });

  // On a direct cart/page load, clean duplicates/wrong tiers only when
  // protection is already present. This never opts the customer in.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => normalizePlan(), { once: true });
  } else {
    normalizePlan();
  }
})();
